import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Media } from '@/mocks/media';

interface HeroCarouselProps {
  items: Media[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const total = items.length;

  useEffect(() => {
    if (total <= 1) return;
    const timer = setInterval(() => setIndex((v) => (v + 1) % total), 5500);
    return () => clearInterval(timer);
  }, [total]);

  if (total === 0) {
    return (
      <div className="w-full h-[420px] md:h-[600px] flex items-center justify-center bg-primary-900 text-background-100">
        暂无推荐影视，敬请期待
      </div>
    );
  }

  const go = (dir: number) => setIndex((v) => (v + dir + total) % total);
  const active = items[index];

  return (
    <section className="relative w-full h-[440px] md:h-[600px] overflow-hidden bg-primary-950">
      {items.map((item, i) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${i === index ? 'opacity-100' : 'opacity-0'}`}
        >
          <img
            src={item.backdrop ?? item.cover}
            alt={`${item.title} 剧照`}
            title={`${item.title} - 最新影视推荐`}
            className="w-full h-full object-cover object-top"
          />
        </div>
      ))}
      <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-950/60 to-primary-950/20"></div>
      <div className="absolute inset-0 bg-gradient-to-t from-primary-950/80 via-transparent to-primary-950/40"></div>

      <div className="relative z-10 w-full h-full px-4 md:px-8 flex items-center">
        <div key={active.id} className="max-w-xl animate-fade-up">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-500 text-primary-950 text-xs font-bold mb-4">
            <i className="ri-fire-fill"></i>
            焦点推荐
          </span>
          <h2 className="font-heading font-bold text-background-50 text-3xl md:text-5xl leading-tight">{active.title}</h2>
          <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-background-200">
            <span className="flex items-center gap-1 text-accent-400 font-semibold">
              <i className="ri-star-fill"></i> {active.rating.toFixed(1)}
            </span>
            <span>{active.year}</span>
            <span className="w-1 h-1 rounded-full bg-background-400"></span>
            <span>{active.region}</span>
            <span className="w-1 h-1 rounded-full bg-background-400"></span>
            <span>{active.duration}</span>
          </div>
          <p className="mt-4 text-background-200/90 text-sm md:text-base leading-relaxed line-clamp-3">{active.description}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {active.genres.map((g) => (
              <span key={g} className="px-2.5 py-1 rounded-full bg-background-50/10 border border-background-50/20 text-background-100 text-xs">
                {g}
              </span>
            ))}
          </div>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link
              to={`/detail/${active.id}`}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-accent-500 text-primary-950 text-sm font-semibold hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-play-circle-line text-lg"></i>
              查看详情
            </Link>
            <Link
              to={active.type === 'movie' ? '/category/movie' : '/category/tv'}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-background-50/10 border border-background-50/30 text-background-50 text-sm font-semibold hover:bg-background-50/20 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-film-line text-lg"></i>
              更多同类
            </Link>
          </div>
        </div>
      </div>

      <button
        type="button"
        aria-label="上一张"
        onClick={() => go(-1)}
        className="hidden md:flex absolute left-4 md:left-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 items-center justify-center rounded-full bg-primary-950/50 text-background-50 hover:bg-accent-500 hover:text-primary-950 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-2xl"></i>
      </button>
      <button
        type="button"
        aria-label="下一张"
        onClick={() => go(1)}
        className="hidden md:flex absolute right-4 md:right-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 items-center justify-center rounded-full bg-primary-950/50 text-background-50 hover:bg-accent-500 hover:text-primary-950 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-2xl"></i>
      </button>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
        {items.map((item, i) => (
          <button
            key={item.id}
            type="button"
            aria-label={`切换到第 ${i + 1} 张`}
            onClick={() => setIndex(i)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${i === index ? 'w-8 bg-accent-500' : 'w-3 bg-background-50/50 hover:bg-background-50/80'}`}
          />
        ))}
      </div>
    </section>
  );
}